// User profile settings page
import { NicknameSelectionModal } from "@/components/NicknameSelectionModal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { getIdToken } from "@/lib/firebase";
import { queryClient } from "@/lib/queryClient";
import { updateUserProfile } from "@/lib/userService";
import { FREE_TIER_LIMITS } from "@/types/user";
import { categories } from "@shared/schema";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  Crown,
  Eye,
  Loader2,
  Mail,
  TrendingUp,
  User,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation } from "wouter";

interface EmailContent {
  isPremium: boolean;
  vocabulary: Array<{ word: string; korean: string; example: string }>;
  quizzes: Array<{
    question: string;
    options: string[];
    correct: string;
    explanation: string;
  }>;
  summary: { english: string; korean: string };
  articles: Array<{
    id: string;
    titleEn: string;
    titleKr: string;
    summaryEn: string;
    category: string;
  }>;
}

export default function Profile() {
  const { user, userProfile, loading, isAuthenticated, refetchProfile } =
    useAuth();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [showNicknameModal, setShowNicknameModal] = useState(false);

  // Fetch user info including nickname
  const { data: userInfo } = useQuery({
    queryKey: ["/api/me", user?.uid],
    queryFn: async () => {
      if (!user) return null;
      const token = await getIdToken();
      if (!token) return null;
      const res = await fetch("/api/me", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!user && isAuthenticated,
  });

  const [emailEnabled, setEmailEnabled] = useState(false);
  const [emailFrequency, setEmailFrequency] = useState<"daily" | "weekly">(
    "daily"
  );
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [emailContent, setEmailContent] = useState<EmailContent | null>(null);

  useEffect(() => {
    if (!loading && !isAuthenticated) {
      setLocation("/");
    }
  }, [loading, isAuthenticated, setLocation]);

  useEffect(() => {
    if (userProfile) {
      setEmailEnabled(!!userProfile.emailFrequency);
      setEmailFrequency(userProfile.emailFrequency || "daily");
      setSelectedCategories(userProfile.preferredCategories);
    }
  }, [userProfile]);

  const handleSaveSettings = async () => {
    if (!userProfile) return;

    try {
      setIsSaving(true);
      await updateUserProfile(userProfile.uid, {
        emailFrequency: emailEnabled ? emailFrequency : null,
        preferredCategories: selectedCategories,
      });
      await refetchProfile();
      toast({
        title: "설정 저장 완료",
        description: "프로필이 성공적으로 업데이트되었습니다",
      });
    } catch (error: any) {
      toast({
        title: "저장 실패",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const toggleCategory = (categoryId: string) => {
    setSelectedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handlePreviewEmail = async () => {
    if (!userProfile) return;

    if (selectedCategories.length === 0) {
      toast({
        title: "카테고리를 선택하세요",
        description: "먼저 관심 카테고리를 선택해주세요",
        variant: "destructive",
      });
      return;
    }

    try {
      setPreviewLoading(true);
      const response = await fetch("/api/generate-email-content", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userId: userProfile.uid }),
      });

      if (!response.ok) {
        throw new Error("Failed to generate email content");
      }

      const data = await response.json();
      setEmailContent(data as EmailContent);
      setShowPreview(true);
    } catch (error: any) {
      toast({
        title: "미리보기 생성 실패",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setPreviewLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user || !userProfile) {
    return null;
  }

  const isPremium = userProfile.role === "premium";
  const usagePercent = {
    wordLookups:
      (userProfile.usageTracking.wordLookups /
        FREE_TIER_LIMITS.wordLookupsPerDay) *
      100,
    quizzes:
      (userProfile.usageTracking.quizzesTaken /
        FREE_TIER_LIMITS.quizzesPerWeek) *
      100,
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
      <div className="space-y-4">
        <Button
          variant="ghost"
          onClick={() => setLocation("/mypage")}
          className="gap-2"
          data-testid="button-back-to-mypage"
        >
          <ArrowLeft className="h-4 w-4" />
          마이페이지로 돌아가기
        </Button>

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">프로필 설정</h1>
            <p className="text-muted-foreground">
              계정 정보 및 학습 설정을 관리하세요
            </p>
          </div>
          {isPremium && (
            <Badge variant="default" className="text-sm px-3 py-1">
              <Crown className="h-4 w-4 mr-1" />
              프리미엄
            </Badge>
          )}
        </div>
      </div>

      {/* Nickname Card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            닉네임
          </CardTitle>
          <CardDescription>동네에서 사용할 별명을 설정하세요</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              {userInfo?.nicknameDisplay || userInfo?.nickname ? (
                <div>
                  <p className="text-lg font-semibold">
                    {userInfo.nicknameDisplay || userInfo.nickname}
                  </p>
                  {userInfo.suburb && (
                    <p className="text-sm text-muted-foreground">
                      {userInfo.suburb}
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-muted-foreground">
                  닉네임이 설정되지 않았습니다
                </p>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => setShowNicknameModal(true)}
            >
              {userInfo?.nicknameDisplay || userInfo?.nickname
                ? "변경하기"
                : "설정하기"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Account Info */}
      <Card>
        <CardHeader>
          <CardTitle>계정 정보</CardTitle>
          <CardDescription>
            Firebase 계정으로 로그인되어 있습니다
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground">이메일</Label>
              <p className="font-medium" data-testid="text-user-email">
                {user.email}
              </p>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">이름</Label>
              <p className="font-medium" data-testid="text-user-name">
                {user.displayName || "미설정"}
              </p>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">회원 등급</Label>
              <p className="font-medium" data-testid="text-user-role">
                {isPremium ? "프리미엄 회원" : "무료 회원"}
              </p>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">가입일</Label>
              <p className="font-medium" data-testid="text-user-created">
                {new Date(userProfile.createdAt).toLocaleDateString("ko-KR")}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Usage Stats (Free Tier Only) */}
      {!isPremium && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              이번 주 사용량
            </CardTitle>
            <CardDescription>무료 회원 제한 내에서 사용하세요</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>단어 뜻 보기 (오늘)</span>
                <span className="font-medium" data-testid="text-usage-words">
                  {userProfile.usageTracking.wordLookups} /{" "}
                  {FREE_TIER_LIMITS.wordLookupsPerDay}
                </span>
              </div>
              <div className="w-full bg-secondary rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{
                    width: `${Math.min(usagePercent.wordLookups, 100)}%`,
                  }}
                />
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>퀴즈 풀기 (이번 주)</span>
                <span className="font-medium" data-testid="text-usage-quizzes">
                  {userProfile.usageTracking.quizzesTaken} /{" "}
                  {FREE_TIER_LIMITS.quizzesPerWeek}
                </span>
              </div>
              <div className="w-full bg-secondary rounded-full h-2">
                <div
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{ width: `${Math.min(usagePercent.quizzes, 100)}%` }}
                />
              </div>
            </div>

            <div className="pt-4 border-t">
              <Button
                variant="default"
                className="w-full"
                onClick={() => setLocation("/pricing")}
                data-testid="button-upgrade"
              >
                <Crown className="h-4 w-4 mr-2" />
                프리미엄으로 업그레이드하고 무제한 사용하기
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Email Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            이메일 학습 콘텐츠
          </CardTitle>
          <CardDescription>
            {isPremium
              ? "프리미엄 회원은 전체 학습 콘텐츠를 이메일로 받을 수 있습니다"
              : "무료 회원은 부분 콘텐츠만 이메일로 받을 수 있습니다"}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="email-enabled">이메일 수신</Label>
              <p className="text-sm text-muted-foreground">
                선택한 카테고리의 학습 콘텐츠를 이메일로 받기
              </p>
            </div>
            <Switch
              id="email-enabled"
              checked={emailEnabled}
              onCheckedChange={setEmailEnabled}
              data-testid="switch-email-enabled"
            />
          </div>

          {emailEnabled && (
            <>
              <div className="space-y-2">
                <Label>수신 빈도</Label>
                <Select
                  value={emailFrequency}
                  onValueChange={(v) =>
                    setEmailFrequency(v as "daily" | "weekly")
                  }
                >
                  <SelectTrigger data-testid="select-email-frequency">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">매일</SelectItem>
                    <SelectItem value="weekly">주 1회</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>관심 카테고리</Label>
                <div className="flex flex-wrap gap-2">
                  {categories.map((cat) => (
                    <Badge
                      key={cat.id}
                      variant={
                        selectedCategories.includes(cat.id)
                          ? "default"
                          : "outline"
                      }
                      className="cursor-pointer"
                      onClick={() => toggleCategory(cat.id)}
                      data-testid={`badge-category-${cat.id}`}
                    >
                      {cat.label}
                    </Badge>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  선택한 카테고리의 기사에서 학습 콘텐츠가 생성됩니다
                </p>
              </div>
            </>
          )}

          <div className="flex gap-2">
            <Button
              onClick={handleSaveSettings}
              disabled={isSaving}
              className="flex-1"
              data-testid="button-save-settings"
            >
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              설정 저장
            </Button>
            {emailEnabled && selectedCategories.length > 0 && (
              <Button
                onClick={handlePreviewEmail}
                disabled={previewLoading}
                variant="outline"
                className="flex-1"
                data-testid="button-preview-email"
              >
                {previewLoading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Eye className="mr-2 h-4 w-4" />
                )}
                이메일 미리보기
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Email Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>이메일 학습 콘텐츠 미리보기</DialogTitle>
            <DialogDescription>
              {isPremium ? "프리미엄 회원" : "무료 회원"} -{" "}
              {emailContent?.isPremium ? "전체 콘텐츠" : "부분 콘텐츠"}
            </DialogDescription>
          </DialogHeader>

          {emailContent && (
            <div className="space-y-6">
              {/* Summary */}
              <div>
                <h3 className="font-semibold text-lg mb-2">오늘의 뉴스 요약</h3>
                <div className="bg-secondary p-4 rounded-md space-y-2">
                  <p className="text-sm">{emailContent.summary.korean}</p>
                  <p className="text-sm text-muted-foreground">
                    {emailContent.summary.english}
                  </p>
                </div>
              </div>

              {/* Articles */}
              <div>
                <h3 className="font-semibold text-lg mb-2">
                  선택된 기사 ({emailContent.articles.length}개)
                </h3>
                <div className="space-y-2">
                  {emailContent.articles.map((article) => (
                    <div key={article.id} className="border p-3 rounded-md">
                      <h4 className="font-medium text-sm">{article.titleKr}</h4>
                      <p className="text-xs text-muted-foreground">
                        {article.titleEn}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Vocabulary */}
              <div>
                <h3 className="font-semibold text-lg mb-2">
                  주요 단어 ({emailContent.vocabulary.length}개)
                  {!isPremium && (
                    <span className="text-sm text-muted-foreground ml-2">
                      (무료 회원: 3개 제한)
                    </span>
                  )}
                </h3>
                <div className="space-y-3">
                  {emailContent.vocabulary.map((vocab, idx) => (
                    <div key={idx} className="border-l-4 border-primary pl-3">
                      <div className="font-medium">{vocab.word}</div>
                      <div className="text-sm text-muted-foreground">
                        {vocab.korean}
                      </div>
                      <div className="text-sm italic mt-1">
                        예문: {vocab.example}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Quizzes */}
              <div>
                <h3 className="font-semibold text-lg mb-2">
                  퀴즈 ({emailContent.quizzes.length}문제)
                  {!isPremium && (
                    <span className="text-sm text-muted-foreground ml-2">
                      (무료 회원: 2문제 제한)
                    </span>
                  )}
                </h3>
                <div className="space-y-4">
                  {emailContent.quizzes.map((quiz, idx) => (
                    <div key={idx} className="bg-secondary p-4 rounded-md">
                      <div className="font-medium mb-2">
                        Q{idx + 1}: {quiz.question}
                      </div>
                      <div className="space-y-1 ml-4">
                        {quiz.options.map((option, optIdx) => (
                          <div
                            key={optIdx}
                            className={`text-sm ${
                              String.fromCharCode(65 + optIdx) === quiz.correct
                                ? "text-green-600 font-medium"
                                : ""
                            }`}
                          >
                            {String.fromCharCode(65 + optIdx)}) {option}
                          </div>
                        ))}
                      </div>
                      <div className="text-sm text-muted-foreground mt-2">
                        정답: {quiz.correct} - {quiz.explanation}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {!isPremium && (
                <div className="bg-primary/10 p-4 rounded-md border border-primary/20">
                  <div className="flex items-start gap-2">
                    <Crown className="h-5 w-5 text-primary mt-0.5" />
                    <div>
                      <div className="font-medium">
                        프리미엄으로 업그레이드하세요!
                      </div>
                      <div className="text-sm text-muted-foreground mt-1">
                        프리미엄 회원은 단어 10개, 퀴즈 5문제 등 전체 학습
                        콘텐츠를 받을 수 있습니다.
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Premium Upgrade CTA (Free Tier Only) */}
      {!isPremium && (
        <Card className="border-primary/50 bg-primary/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Crown className="h-5 w-5 text-primary" />
              프리미엄으로 업그레이드
            </CardTitle>
            <CardDescription>
              무제한 학습 기능과 완전한 이메일 콘텐츠를 이용하세요
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <ul className="space-y-2 text-sm">
              <li className="flex items-center gap-2">
                <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                <span>무제한 단어 저장 및 학습</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                <span>무제한 리딩 퀴즈 및 상세 해설</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                <span>무제한 플래시 카드 학습</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                <span>완전한 이메일 학습 콘텐츠</span>
              </li>
              <li className="flex items-center gap-2">
                <div className="h-1.5 w-1.5 rounded-full bg-primary" />
                <span>광고 제거</span>
              </li>
            </ul>
            <Button
              variant="default"
              className="w-full"
              onClick={() => setLocation("/pricing")}
              data-testid="button-pricing"
            >
              요금제 보기
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Nickname Selection Modal */}
      <NicknameSelectionModal
        open={showNicknameModal}
        onOpenChange={setShowNicknameModal}
        onSuccess={async () => {
          // Refetch user info to get updated nickname
          await queryClient.invalidateQueries({
            queryKey: ["/api/me", user?.uid],
          });
          await refetchProfile();
          toast({
            title: "닉네임이 설정되었습니다",
            description: "변경사항이 적용되었습니다",
          });
        }}
        currentNickname={userInfo?.nicknameDisplay || userInfo?.nickname}
      />
    </div>
  );
}
